import { TeamSummary } from '@/contexts/AuthContext';

const KNOWN_STATUSES = ['ACTIVE', 'INACTIVE', 'PAST_DUE'];
const KNOWN_PLANS = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

export type TeamOverlayFields = Pick<TeamSummary, 'status' | 'plan' | 'monthlyLimit' | 'memberCount'>;

/** WorkspaceStatus -> 레거시 TeamStatus 문자열 매핑 (기존 'PAST_DUE' -> 'SUSPENDED' 이름 변경 흡수) */
function mapStatus(status: string): string {
  return status === 'PAST_DUE' ? 'SUSPENDED' : status;
}

function isValidRow(row: any, legacyTeamIds: Set<number>): boolean {
  if (row == null || typeof row !== 'object') return false;
  if (row.legacyTeamId == null) return true; // 개인 워크스페이스 - 팀 목록 필터링 단계에서 이미 제외됨
  if (typeof row.legacyTeamId !== 'number' || !legacyTeamIds.has(row.legacyTeamId)) return false;
  if (typeof row.status !== 'string' || !KNOWN_STATUSES.includes(row.status)) return false;
  if (typeof row.plan !== 'string' || !KNOWN_PLANS.includes(row.plan)) return false;
  if (typeof row.monthlyLimit !== 'number' || Number.isNaN(row.monthlyLimit)) return false;
  if (typeof row.memberCount !== 'number' || Number.isNaN(row.memberCount)) return false;
  return true;
}

/**
 * workspaceApi.getMyTeams() 응답을 legacy myTeams 위에 덮어씌울 overlay로 검증·변환한다.
 *
 * all-or-nothing 검증: 배열이 아니거나, (개인 워크스페이스를 제외한) 팀 개수가 legacy와
 * 다르거나, 행 하나라도 status/plan/monthlyLimit/memberCount 중 하나라도 이상하면 전체를
 * 버리고 빈 overlay({})를 반환한다 - 일부 팀만 덮어써지는 상황을 만들지 않기 위함
 * (2026-07-12 사용자 지적 반영: 개수만 맞고 필드가 깨진 "부분 실패"도 실패로 취급).
 */
export function validateAndBuildOverlay(
  rows: unknown,
  myTeams: TeamSummary[]
): Record<number, TeamOverlayFields> {
  if (!Array.isArray(rows)) return {};

  const teamRows = rows.filter((row: any) => row != null && row.legacyTeamId != null);
  if (teamRows.length !== myTeams.length) return {};

  const legacyTeamIds = new Set(myTeams.map((t) => t.teamId));
  const overlay: Record<number, TeamOverlayFields> = {};

  for (const row of teamRows) {
    if (!isValidRow(row, legacyTeamIds)) return {};
    overlay[row.legacyTeamId] = {
      status: mapStatus(row.status),
      plan: row.plan,
      monthlyLimit: row.monthlyLimit,
      memberCount: row.memberCount,
    };
  }

  return overlay;
}
